/**
 * URL routing utilities for Bible reader app
 * Manages browser history without external router libraries
 */

export interface AppRoute {
  type: 'reader' | 'search' | 'reading-plan';
  bookId?: string;
  chapter?: number;
  verse?: number;
  searchQuery?: string;
  translation?: string;
  planId?: string;
}

/**
 * Parse current URL to determine app state
 */
export function parseCurrentUrl(): AppRoute {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const translation = searchParams.get('t') || undefined; // undefined when not in URL

  // Handle search routes: /search?q=...
  if (path === '/search') {
    const query = searchParams.get('q');
    if (query) {
      return {
        type: 'search',
        searchQuery: decodeURIComponent(query),
        translation
      };
    }
  }

  const pathSegments = path.split('/').filter(Boolean);

  // Handle reading plan routes: /plan/<planId>
  if (pathSegments.length === 2 && pathSegments[0] === 'plan') {
    return {
      type: 'reading-plan',
      planId: pathSegments[1],
      translation
    };
  }

  // Handle reader routes: /<bookId>/<chapter> with optional ?v=<verse>&t=<translation>
  if (pathSegments.length === 2) {
    const [bookId, chapterStr] = pathSegments;
    const chapter = parseInt(chapterStr, 10);
    const verse = searchParams.get('v');

    if (bookId && !isNaN(chapter)) {
      return {
        type: 'reader',
        bookId: bookId.toUpperCase(),
        chapter,
        verse: verse ? parseInt(verse, 10) : undefined,
        translation
      };
    }
  }

  // Default route: Genesis 1
  return {
    type: 'reader',
    bookId: 'GEN',
    chapter: 1,
    translation
  };
}

/**
 * Update URL to match current app state
 */
export function updateUrl(route: AppRoute, replace: boolean = false): void {
  let url: string;
  
  // Build search params
  const params = new URLSearchParams();
  if (route.translation && route.translation !== 'NET') {
    params.set('t', route.translation);
  }
  
  if (route.type === 'search' && route.searchQuery) {
    params.set('q', route.searchQuery);
    url = '/search';
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  } else if (route.type === 'reading-plan' && route.planId) {
    url = `/plan/${route.planId}`;
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  } else if (route.type === 'reader' && route.bookId && route.chapter) {
    if (route.verse) {
      params.set('v', route.verse.toString());
    }
    url = `/${route.bookId}/${route.chapter}`;
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  } else {
    // Default fallback
    url = '/GEN/1';
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  }

  const currentUrl = window.location.pathname + window.location.search;
  if (currentUrl !== url) {
    if (replace) {
      window.history.replaceState(null, '', url);
    } else {
      window.history.pushState(null, '', url);
    }
  }
}

/**
 * Navigate to a book/chapter, updating both state and URL
 */
export function navigateToChapter(bookId: string, chapter: number, verse?: number): void {
  updateUrl({
    type: 'reader',
    bookId: bookId.toUpperCase(),
    chapter,
    verse
  });
}

/**
 * Navigate to search results, updating both state and URL
 */
export function navigateToSearch(query: string): void {
  updateUrl({
    type: 'search',
    searchQuery: query
  });
}

/**
 * Navigate to default home (Genesis 1)
 */
export function navigateToHome(): void {
  updateUrl({
    type: 'reader',
    bookId: 'GEN',
    chapter: 1
  });
}